/**
 * 4: アイコンプレビューステップ
 *
 * - 生成中: CircularProgress で進捗表示
 * - 生成完了: 生成された画像を表示 + 追加プロンプト入力
 * - フッターは親（CreateUserWizard）で管理
 */
import { useState, useCallback } from "react";
import I18n from "common/i18n";
import { UserAvatar } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { GenerationStatus } from "features/generation/types";
import { CircularProgress } from "features/generation/components/CircularProgress";

interface IconPreviewStepProps {
  status: GenerationStatus;
  progress: number;
  generatedImage: string | null;
  partialImage: string | null;
  additionalPrompt?: string;
  onAdditionalPromptChange?: (prompt: string) => void;
  onRegenerate?: () => void;
}

export const IconPreviewStep = ({
  status,
  progress,
  generatedImage,
  partialImage,
  additionalPrompt = "",
  onAdditionalPromptChange,
  onRegenerate,
}: IconPreviewStepProps) => {
  const play = usePlaySound();
  const [localPrompt, setLocalPrompt] = useState(additionalPrompt);
  const isGenerating = status === GenerationStatus.GENERATING;
  const isComplete = status === GenerationStatus.COMPLETE;
  const displayImage = generatedImage || partialImage;

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.slice(0, 20);
      setLocalPrompt(value);
      onAdditionalPromptChange?.(value);
    },
    [onAdditionalPromptChange],
  );

  const handleRegenerate = useCallback(() => {
    play();
    btnDelay(() => {
      onRegenerate?.();
    });
  }, [play, onRegenerate]);

  return (
    <div className="flex-col-el flex-center w-full h-full">
      {/* プレビュー領域 */}
      {displayImage && !isGenerating ? (
        <UserAvatar size={200} icon={displayImage} />
      ) : (
        <div className="flex-col-el flex-center w-[200px] h-[200px] bg-beige-20 rounded-full border-4 border-gray-20 overflow-hidden">
          {isGenerating ? (
            <CircularProgress progress={progress} size={184} />
          ) : (
            <span className="material-symbols-outlined text-gray-40 !text-[80px]">
              auto_awesome
            </span>
          )}
        </div>
      )}

      {/* メッセージ */}
      <p className="text-body text-textcolor-black text-[24px] mt-6">
        {isGenerating ? I18n.t("MSG_ICON_GENERATING") : I18n.t("MSG_ICON_CONFIRM_PROMPT")}
      </p>

      {/* 追加プロンプト入力フォーム（生成完了後のみ表示） */}
      {isComplete && onRegenerate && (
        <div className="flex-row-el shrink-0 items-center justify-center gap-4 mt-6">
          <div className="relative w-[400px]">
            <input
              type="text"
              value={localPrompt}
              onChange={handlePromptChange}
              placeholder={I18n.t("MSG_ICON_ADDITIONAL_PROMPT_PLACEHOLDER")}
              maxLength={20}
              className="input-modal text-body text-textcolor-black text-[24px] w-full h-[56px] pr-[60px]"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[18px] text-gray-80">
              {localPrompt.length}/20
            </span>
          </div>
          <div className="alpha">
            <button className="btn btn-primary" onClick={handleRegenerate}>
              <div className="flex-row-el flex-center gap-1 mb-[3px]">
                <span className="material-symbols-outlined !text-4xl">auto_awesome</span>
                <p className="text-body text-white text-font-size-body1">
                  {I18n.t("MSG_ICON_CREATE_BTN")}
                </p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
